// ============================================
// restaurant/routes/RestaurantRoutes.js
// Dedicated Restaurant Sub-Router
// ============================================

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../common/context/AuthContext';
import PrivateRoute from '../../common/routes/PrivateRoute';

// Auth Components
import RestaurantSignIn from '../auth/RestaurantSignIn';
import RestaurantSignUp from '../auth/RestaurantSignUp';
import RestaurantForgotPassword from '../auth/RestaurantForgotPassword';
import PinLogin from '../auth/PinLogin';
import LoginMethodSelection from '../auth/LoginMethodSelection';

// UI Components
import OutletSelection from '../components/OutletSelection';
import Info from '../components/Info';
import AccountDelete from '../components/AccountDelete';

// Pages
import Dashboard from '../pages/Dashboard';
import ProductManager from '../pages/Product';
import ExpenseTracker from '../pages/ExpenseTracker';
import NewBill from '../sales/NewBill';
import MenuSelection from '../sales/MenuSelection';
import PrintKOT from '../sales/PrintKOT';
import KOT from '../sales/KOT';
import TableManager from '../sales/ManageTable';
import ExpenseReport from '../reports/ExpenseReport';
import SalesReport from '../reports/SalesReport';
import MonthlyGSTReport from '../reports/MonthlyGSTReport';
import PeakHoursAnalytics from '../reports/PeakHoursAnalytics';
import SubscriptionPage from '../../common/context/SubscriptionPage';
import ViewSubscriptionInfo from '../../common/context/ViewSubscriptionInfo';
import Settings from '../settings/Settings';
import NotificationSettings from '../settings/NotificationSettings';
import BillHistory from '../sales/BillHistory';
import BillDetails from '../sales/BillDetails';
import PrintBill from '../billing/PrintBill';
import TakeawayPrintBill from '../billing/TakeawayPrintBill';
import PinProtectionPage from '../settings/settingModal/PinProtectionPage';
import SetupAdminPin from '../settings/settingModal/SetupAdminPin';
import SetupManagerPin from '../settings/settingModal/SetupManagerPin';
import ProtectedRoute from '../settings/settingModal/ProtectedRoute';
import ChangeAdminPin from '../settings/settingModal/ChangeAdminPin';
import ChangeManagerPin from '../settings/settingModal/ChangeManagerPin';

export default function RestaurantRoutes() {
  const { isAuthenticated, getBusinessType } = useAuth();
  const businessType = getBusinessType ? getBusinessType() : (localStorage.getItem('businessType') || 'restaurant');
  const isSalon = isAuthenticated && businessType === 'salon';

  return (
    <Routes>
      {/* Auth */}
      <Route path="/signup" element={!isAuthenticated ? <RestaurantSignUp /> : <Navigate to={isSalon ? "/salon/dashboard" : "/dashboard"} />} />
      <Route path="/restaurant/signup" element={!isAuthenticated ? <RestaurantSignUp /> : <Navigate to="/dashboard" />} />
      <Route path="/signin" element={!isAuthenticated ? <RestaurantSignIn /> : <Navigate to={isSalon ? "/salon/dashboard" : "/dashboard"} />} />
      <Route path="/restaurant/signin" element={!isAuthenticated ? <RestaurantSignIn /> : <Navigate to="/dashboard" />} />
      <Route path="/forgot-password" element={<RestaurantForgotPassword />} />
      <Route path="/restaurant/forgot-password" element={<RestaurantForgotPassword />} />
      <Route path="/pinlogin" element={!isAuthenticated ? <PinLogin /> : <Navigate to={isSalon ? "/salon/dashboard" : "/dashboard"} />} />
      <Route path="/login-method-selection" element={!isAuthenticated ? <LoginMethodSelection /> : <Navigate to={isSalon ? "/salon/dashboard" : "/dashboard"} />} />
      <Route path="/outlet-selection" element={<OutletSelection />} />
      <Route path="/info" element={<Info />} />
      <Route path="/account-delete" element={<AccountDelete />} />
      <Route path="/delete-account" element={<AccountDelete />} />

      {/* Restaurant Operations */}
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/product" element={<PrivateRoute><ProtectedRoute requiredModule="inventory"><ProductManager /></ProtectedRoute></PrivateRoute>} />
      <Route path="/expense" element={<PrivateRoute><ProtectedRoute requiredModule="expense_reports"><ExpenseTracker /></ProtectedRoute></PrivateRoute>} />
      <Route path="/new-bill" element={<PrivateRoute><ProtectedRoute requiredModule="billing"><NewBill /></ProtectedRoute></PrivateRoute>} />
      <Route path="/menu-selection" element={<PrivateRoute><ProtectedRoute requiredModule="billing"><MenuSelection /></ProtectedRoute></PrivateRoute>} />
      <Route path="/print-kot/:tableId" element={<PrivateRoute><ProtectedRoute requiredModule="billing"><PrintKOT /></ProtectedRoute></PrivateRoute>} />
      <Route path="/kot" element={<PrivateRoute><ProtectedRoute requiredModule="billing"><KOT /></ProtectedRoute></PrivateRoute>} />
      <Route path="/table-manager" element={<PrivateRoute><ProtectedRoute requiredModule="inventory"><TableManager /></ProtectedRoute></PrivateRoute>} />

      {/* Reports & Protected Modules */}
      <Route path="/sales-report" element={<PrivateRoute><ProtectedRoute requiredModule="sales_reports"><SalesReport /></ProtectedRoute></PrivateRoute>} />
      <Route path="/expense-report" element={<PrivateRoute><ProtectedRoute requiredModule="expense_reports"><ExpenseReport /></ProtectedRoute></PrivateRoute>} />
      <Route path="/gst-report" element={<PrivateRoute><ProtectedRoute requiredModule="gst_reports"><MonthlyGSTReport /></ProtectedRoute></PrivateRoute>} />
      <Route path="/peak-hours" element={<PrivateRoute><ProtectedRoute requiredModule="analytics"><PeakHoursAnalytics /></ProtectedRoute></PrivateRoute>} />

      {/* Subscriptions & Settings */}
      <Route path="/subscription" element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
      <Route path="/view-subscription" element={<PrivateRoute><ViewSubscriptionInfo /></PrivateRoute>} />
      <Route path="/ViewSubscriptionInfo" element={<PrivateRoute><ViewSubscriptionInfo /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><ProtectedRoute requiredModule="settings"><Settings /></ProtectedRoute></PrivateRoute>} />
      <Route path="/notification-settings" element={<PrivateRoute><ProtectedRoute requiredModule="settings"><NotificationSettings /></ProtectedRoute></PrivateRoute>} />
      <Route path="/bill-history" element={<PrivateRoute><ProtectedRoute requiredModule="billing"><BillHistory /></ProtectedRoute></PrivateRoute>} />
      <Route path="/bill-details/:billId" element={<PrivateRoute><ProtectedRoute requiredModule="billing"><BillDetails /></ProtectedRoute></PrivateRoute>} />
      <Route path="/print-bill/:billId" element={<PrivateRoute><PrintBill /></PrivateRoute>} />
      <Route path="/takeaway-print-bill/:billId" element={<PrivateRoute><TakeawayPrintBill /></PrivateRoute>} />

      {/* Security Modals */}
      <Route path="/pin-protection" element={<PrivateRoute><ProtectedRoute requiredModule="settings"><PinProtectionPage /></ProtectedRoute></PrivateRoute>} />
      <Route path="/setup-admin-pin" element={<PrivateRoute><ProtectedRoute requiredModule="settings"><SetupAdminPin /></ProtectedRoute></PrivateRoute>} />
      <Route path="/setup-manager-pin" element={<PrivateRoute><ProtectedRoute requiredModule="settings"><SetupManagerPin /></ProtectedRoute></PrivateRoute>} />
      <Route path="/change-admin-pin" element={<PrivateRoute><ProtectedRoute requiredModule="settings"><ChangeAdminPin /></ProtectedRoute></PrivateRoute>} />
      <Route path="/change-manager-pin" element={<PrivateRoute><ProtectedRoute requiredModule="settings"><ChangeManagerPin /></ProtectedRoute></PrivateRoute>} />
    </Routes>
  );
}
